import { useAsync } from 'react-async';
import { decodeId } from 'utils';
import {
  ProgramAccommodationTypeEnum,
  ExportDayHotelRoomTagEnum,
  SustainableLevelEnum,
} from '@koob/enums';
import {
  translateAccommodationLabel,
  translateMealPlan,
} from '../accommodationLabels';
import ExportDayHotelCard from './ExportDayHotelCard';
import ExportDayHotelFallback from './ExportDayHotelFallback';

const REFUND_TAGS = new Set(Object.values(ExportDayHotelRoomTagEnum));

function sustainableLeafCount(level) {
  return SustainableLevelEnum[level?.toUpperCase?.()] ?? 0;
}

function extractRoomInfo(hotelSelectedRoomKey) {
  if (!hotelSelectedRoomKey) return null;

  const key = hotelSelectedRoomKey.replace(/&amp;/g, ' & ');
  const parts = key.split('_');
  const roomName = parts[0].trim();

  const priceIdx = parts.findIndex((p, i) => i > 1 && /^\d+(\.\d+)?$/.test(p));
  if (priceIdx === -1) return null;

  // The tags run up to the promos ("_[…]") and may contain underscores
  // themselves ("room_refundable").
  const tagsRaw = parts
    .slice(priceIdx + 2)
    .join('_')
    .split('_[')[0];
  const mealPlan =
    tagsRaw.split('#').find((t) => t && !REFUND_TAGS.has(t)) || '';

  const mealPlans = [
    mealPlan
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/(\w)(included|excluded)/gi, '$1 $2'),
  ].filter(Boolean);

  return { roomName, mealPlans };
}

export function getHotelStayInfo(tripDays, index) {
  const currentId = decodeId(tripDays?.[index]?.hotelId);
  if (!currentId) return { nightIndex: 1, totalNights: 1, isFirstNight: true };

  let start = index;
  while (start > 0 && decodeId(tripDays[start - 1]?.hotelId) === currentId)
    start -= 1;
  let end = index;
  while (
    end < tripDays.length - 1 &&
    decodeId(tripDays[end + 1]?.hotelId) === currentId
  )
    end += 1;

  return {
    nightIndex: index - start + 1,
    totalNights: end - start + 1,
    isFirstNight: index === start,
  };
}

export function hasHotelBlock({ hotel, day, program }) {
  return (
    !!hotel ||
    !!day?.hotelBookedOnMyOwn ||
    program?.accomodationType ===
      ProgramAccommodationTypeEnum.NO_ACCOMMODATION ||
    program?.accomodationType === ProgramAccommodationTypeEnum.NIGHT_TRANSFER
  );
}

function ExportDayHotelNightTransfer({
  accommodationExperience,
  getExperience,
  settings,
}) {
  const { data } = useAsync({
    promiseFn: getExperience,
    experienceId: accommodationExperience?.id,
    skip: !getExperience || !accommodationExperience?.id,
  });
  const experience = data?.data;
  const image = experience?.pictures?.[0]?.imageUrl;

  return (
    <ExportDayHotelCard
      name={experience?.name}
      description={experience?.description}
      image={image}
      totalNights={1}
      settings={settings}
    />
  );
}

export default function ExportDayHotel({
  trip,
  index,
  hotel,
  booking,
  program,
  day,
  accommodationLabels,
  settings,
  theme,
  getExperience,
}) {
  const accommodationType =
    program?.accomodationType === ProgramAccommodationTypeEnum.NO_ACCOMMODATION
      ? ProgramAccommodationTypeEnum.NO_ACCOMMODATION
      : day?.hotelBookedOnMyOwn
        ? ProgramAccommodationTypeEnum.BOOKED_ON_MY_OWN
        : program?.accomodationType;

  if (hotel) {
    const { nightIndex, totalNights, isFirstNight } = getHotelStayInfo(
      trip?.tripDays,
      index,
    );

    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

    let roomName, mealPlans;
    if (booking) {
      const roomBooked = booking?.roomsBooked?.[0];
      roomName = roomBooked?.room?.name;
      mealPlans = roomBooked?.room?.bedChoice?.tags
        ?.filter(
          (tag) => tag?.label && !REFUND_TAGS.has(tag.label.toLowerCase()),
        )
        .map((tag) => tag.label);
    } else {
      const info = extractRoomInfo(day?.hotelSelectedRoomKey);
      roomName = info?.roomName;
      mealPlans = info?.mealPlans;
    }
    // Two hotels of the trip may both have a "Deluxe Room".
    roomName = translateAccommodationLabel(
      roomName,
      accommodationLabels?.rooms?.filter(
        (room) => decodeId(room.hotelId) === decodeId(hotel?.id),
      ),
    );
    const mealPlan = mealPlans
      ?.map((part) =>
        capitalize(translateMealPlan(part, accommodationLabels?.mealPlans)),
      )
      .join(', ');

    const hotelDescription = hotel?.hotelDescriptions?.find(
      (elem) => elem.content && elem.description?.isMainDescription,
    );

    return (
      <ExportDayHotelCard
        name={hotel?.displayName}
        address={hotel?.address}
        stars={hotel?.stars}
        sustainableLevel={sustainableLeafCount(hotel?.sustainable?.level)}
        roomName={roomName}
        mealPlan={mealPlan}
        description={hotelDescription?.content}
        image={hotel?.primaryAttachment?.imageUrl}
        isAccessible={hotel?.isAccessible}
        nightIndex={nightIndex}
        totalNights={totalNights}
        condensed={!isFirstNight}
        hotel={hotel}
        theme={theme}
        settings={settings}
      />
    );
  }

  if (
    day?.hotelBookedOnMyOwn ||
    program?.accomodationType === ProgramAccommodationTypeEnum.NO_ACCOMMODATION
  ) {
    return (
      <ExportDayHotelFallback
        accommodationType={accommodationType}
        settings={settings}
      />
    );
  }

  if (
    program?.accomodationType === ProgramAccommodationTypeEnum.NIGHT_TRANSFER
  ) {
    return (
      <ExportDayHotelNightTransfer
        accommodationExperience={program?.accomodationExperience}
        getExperience={getExperience}
        settings={settings}
      />
    );
  }

  return null;
}
